import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Key, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Users = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'client',
    language: 'bg',
    permissions: [],
    department: '',
    phone: ''
  });

  const permissionsList = [
    'dashboard', 'clients', 'employees', 'deals',
    'contracts', 'finances', 'tasks', 'marketing'
  ];

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`${API}/users/${editingUser.id}`, formData);
        toast.success('Потребителят бе обновен!');
      } else {
        // Create new user
        await axios.post(`${API}/auth/register`, formData);
        toast.success('Потребителят бе създаден!');
      }
      fetchUsers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(editingUser ? 'Грешка при обновяване' : 'Грешка при създаване');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '', // Password is optional for editing
      role: user.role,
      language: user.language,
      permissions: user.permissions || [],
      department: user.department || '',
      phone: user.phone || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този потребител?')) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Потребителят бе изтрит!');
      fetchUsers();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'client',
      language: 'bg',
      permissions: [],
      department: '',
      phone: ''
    });
  };

  const togglePermission = (perm) => {
    const updated = formData.permissions.includes(perm)
      ? formData.permissions.filter(p => p !== perm)
      : [...formData.permissions, perm];
    setFormData({...formData, permissions: updated});
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="page-container">
        <h1>Достъп отказан</h1>
        <p>Само администратори имат достъп до това меню.</p>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="users-page">
      <div className="page-header">
        <h1 className="page-title">{t('users')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-user-btn">
              <Plus size={20} /> Създай потребител
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Редактирай потребител' : 'Нов потребител/служител'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="user-form" data-testid="user-form">
              <Input
                type="email"
                placeholder="Имейл адрес"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
              <Input
                placeholder="Име и фамилия"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
              <Input
                type="password"
                placeholder={editingUser ? 'Парола (остави празно за без промяна)' : 'Парола за вход'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required={!editingUser}
              />
              <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Избери роля" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Администратор</SelectItem>
                  <SelectItem value="employee">Служител</SelectItem>
                  <SelectItem value="client">Клиент</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.language} onValueChange={(val) => setFormData({...formData, language: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Избери език" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bg">Български</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ro">Română</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>

              {formData.role === 'employee' && (
                <>
                  <Input
                    placeholder="Отдел (напр: Продажби, Техническа поддръжка)"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                  />
                  <Input
                    placeholder="Телефон за връзка"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                  <div className="permissions-section">
                    <h4>Права на достъп до модули</h4>
                    <div className="permissions-grid">
                      {permissionsList.map(perm => (
                        <label key={perm} className="permission-item">
                          <Checkbox
                            checked={formData.permissions.includes(perm)}
                            onCheckedChange={() => togglePermission(perm)}
                            data-testid={`permission-${perm}`}
                          />
                          <span>{t(perm)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-user-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="users-grid">
        {users.map(user => (
          <div key={user.id} className="user-card" data-testid={`user-card-${user.id}`}>
            <div className="user-card-header">
              <div className="user-avatar">
                {user.name?.charAt(0)}
              </div>
              <div>
                <h3>{user.name}</h3>
                <p className="user-email">{user.email}</p>
              </div>
            </div>

            <div className="user-card-body">
              <div className="user-info-row">
                <span>Роля:</span>
                <span className={`role-badge role-${user.role}`}>
                  {user.role === 'admin' ? 'Администратор' : 
                   user.role === 'masteradmin' ? 'Главен Администратор' : 
                   user.role === 'employee' ? 'Служител' : 'Клиент'}
                </span>
              </div>
              <div className="user-info-row">
                <span>Език:</span>
                <span>{user.language?.toUpperCase()}</span>
              </div>
              {user.role === 'employee' && user.department && (
                <div className="user-info-row">
                  <span>Отдел:</span>
                  <span>{user.department}</span>
                </div>
              )}
              {user.role === 'employee' && user.phone && (
                <div className="user-info-row">
                  <span>Телефон:</span>
                  <span>{user.phone}</span>
                </div>
              )}
              <div className="user-info-row">
                <span>Парола:</span>
                <span>
                  {currentUser?.role === 'masteradmin' && user.password_hash ? (
                    <span title={user.password_hash} style={{cursor: 'pointer'}}>
                      <Key size={16} /> (виж детайли)
                    </span>
                  ) : user.password_hash ? <Key size={16} /> : 'Няма'}
                </span>
              </div>
              {user.role === 'employee' && user.permissions?.length > 0 && (
                <div className="user-permissions">
                  <strong>Права на достъп:</strong>
                  <div className="permissions-tags">
                    {user.permissions.map(perm => (
                      <span key={perm} className="permission-tag">{t(perm)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="user-card-actions">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleEdit(user)}
                data-testid={`edit-user-${user.id}`}
              >
                <Edit size={16} /> Редактирай
              </Button>
              {currentUser?.id !== user.id && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => handleDelete(user.id)}
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 size={16} /> Изтрий
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Users;
